import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { newsletterApi } from '../api/newsletter.api';
import { LEGAL_PATHS } from '../legal/company';

/** Loose on purpose — enough to catch a typo before a round trip; the server's @IsEmail decides. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const NewsletterSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'subscribed'>('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const address = email.trim();
    if (!EMAIL_PATTERN.test(address)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');
    setStatus('submitting');

    try {
      await newsletterApi.subscribe(address);
      setStatus('subscribed');
    } catch (err: any) {
      setStatus('idle');
      // The server's own message for a failed subscription is either a validator
      // string or a rate-limit notice, neither of which reads well here.
      setError(
        err?.response?.status === 429
          ? 'That is a few attempts in a row. Please try again in a few minutes.'
          : 'Could not sign you up just now. Please try again.'
      );
    }
  };

  return (
    <section id="newsletter" className="py-20 px-6 lg:px-12 bg-nova-bg3 border-y border-nova-border">
      <div className="max-w-content mx-auto flex flex-col lg:flex-row lg:items-center gap-10">
        <div className="lg:flex-1">
          <div className="section-tag">Newsletter</div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-nova-text mb-3">
            New features, first
          </h2>
          <p className="text-nova-text2 leading-relaxed max-w-xl">
            An email when we ship something worth knowing about, with practical tips for getting more
            out of your website. A couple a month at most.
          </p>
        </div>

        <div className="lg:w-[26rem]">
          {status === 'subscribed' ? (
            <div role="status" className="nova-card !bg-nova-bg2">
              <p className="font-semibold text-nova-text mb-1">You are on the list.</p>
              <p className="text-sm text-nova-text2 break-words">
                We will write to {email.trim()} when there is something new.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <label htmlFor="newsletter-email" className="nova-label">
                Email address
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  id="newsletter-email"
                  type="email"
                  className="nova-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  autoComplete="email"
                />
                <button
                  type="submit"
                  className="btn-primary flex-shrink-0 py-3 disabled:opacity-60"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Signing you up...' : 'Subscribe'}
                </button>
              </div>
              {error && (
                <p role="alert" className="text-sm text-nova-red mt-3">
                  {error}
                </p>
              )}
              <p className="text-xs text-nova-text3 mt-3 leading-relaxed">
                Your address is used for this newsletter and nothing else, and every email carries an
                unsubscribe link. See our{' '}
                <Link to={LEGAL_PATHS.privacy} className="text-nova-text2 hover:text-nova-sky underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
